import React, { Component } from 'react';
import moment from 'moment';
import clsx from 'clsx';
import { compose } from 'utils/compose';
import { inject, observer } from 'mobx-react';
import { Route, Routes, useParams, useNavigate, useLocation } from 'react-router-dom';

import { Link as MLink, Box, Container, Button, Chip, Grid, Avatar, Typography } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';

import withMediaQuery from 'constants/mediaQuery';
import FooterContainer from 'components/Footer';
import { getCookie } from 'utils/cookie';

import ScanBox from './scan';
import { useStyles } from 'containers/main/styles';

// HOC to inject styles into class component
// no-dd-sa:javascript-code-style/parameter-name
const withUseStyles = (WrappedComponent) => {
  return function WithStylesWrapper(props) {
    const classes = useStyles();
    return <WrappedComponent {...props} classes={classes} />;
  };
};

class ScanContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      scanned: false,
      scanning: false,
      checkingScanStatus: true,
      data: null,
    };
  }

  componentDidMount() {
    const { store, location, match } = this.props;
    const { deliveryStore } = store;

    deliveryStore.getDelivery(match.params.tracking_code, (resp) => {
      if (resp.ok) {
        let token = location.search.replace('?token=', '');
        if (!token || token.trim() === '') {
          token = getCookie('axl_shipment_token');
        }

        store.deliveryStore.isAllowScanId(match.params.tracking_code, token).then((resp) => {
          if (resp.ok) {
            if (!resp.data.allow_scan) {
              this.goBack();
            } else {
              this.setState({ token, shipmentId: resp.data.shipment_id, settings: resp.data.settings });
            }
          } else {
            this.goBack();
          }
        });
      }
    });
  }

  goBack = () => {
    const { navigate, location } = this.props;
    navigate(location.pathname.replace(/\/idscan[^\.]*$/g, ''), { replace: true });
  };

  goToScan = () => {
    const { navigate, location } = this.props;
    navigate(location.pathname + '/scan?token=' + this.state.token, { replace: true });
  };

  scan = (e) => {
    const { store, match } = this.props;

    store.deliveryStore.notifyID(this.state.data.content, match.params.tracking_code, this.state.shipmentId, this.state.token).then((resp) => {
      if (resp.ok) {
        this.goBack();
      }
    });
  };

  clear = (e) => {
    this.setState({
      scanned: false,
      data: null,
    });
  };

  onConfirmScan = (data) => {
    this.setState({ scanned: true });
    this.setState({ data });
  };

  render() {
    const { mediaQuery, classes, store, location, navigate } = this.props;
    const { delivery, trackingCode, settings, hideCompanyBranding } = store.deliveryStore;

    if (!delivery) return null;
    const { client, shipment } = delivery;
    let isScanPage = !!location.pathname && location.pathname.match(/\/scan$/g);

    return (
      <div style={{ height: '100%' }}>
        {!isScanPage && (
          <Container maxWidth={false} disableGutters sx={classes.container}>
            <Container sx={{ mt: 4, mb: 4, ...classes.bodyContainer }}>
              <Box sx={classes.boxWrapper}>
                <Box p={mediaQuery.isMobile ? 2 : 4.5} pb={mediaQuery.isMobile ? 3 : undefined} sx={classes.statusContainer}>
                  <Box mb={2.5} sx={classes.statusTitle}>
                    <Box>
                      <Box component="span" sx={classes.statusText}>
                        Status:{' '}
                      </Box>
                      <Box component="span" sx={classes.statusTextBig}>
                        Identification required
                      </Box>
                    </Box>
                    <Box>{shipment.attempt_count > 1 && <Chip size="small" sx={classes.reattemptButton} label={'2nd attempt'} />}</Box>
                  </Box>
                </Box>
                <Grid container sx={classes.shipmentInfoContainer}>
                  <Grid size={{ xs: 12, sm: 6 }} sx={classes.shipmentInfoLeft}>
                    <Grid container spacing={2} style={{ marginBottom: 15 }}>
                      {!hideCompanyBranding && (
                        <Grid>
                          <Avatar alt="Client Logo" src={client.logo_url} variant="square" sx={classes.clientLogo} />
                        </Grid>
                      )}
                      <Grid>
                        {!hideCompanyBranding && (
                          <Typography sx={{ ...classes.clientName, ...classes.fontWeight700 }} variant="h6">
                            {client.company}
                          </Typography>
                        )}
                        <div>
                          <Box component="span" sx={classes.onSurfaceTextLabel}>
                            Tracking code:{' '}
                          </Box>
                          <Box component="strong" sx={{ ...classes.onSurfaceTextTitle, ...classes.fontWeight600 }}>
                            {trackingCode}
                          </Box>
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={12}>
                    <Typography sx={classes.signTitle}>
                      For this delivery to be completed, we must verify your identity. Please provide a scan of your ID card or driver’s license using
                      the camera function below:
                    </Typography>
                    <Box display="flex" marginTop={1} alignItems="center" justifyContent="center" sx={classes.scanPreviewBox} width="100%">
                      {this.state.data && (
                        <Box style={{ position: 'absolute', top: 10, left: 10 }}>
                          <Typography>
                            {this.state.data.first_name} {this.state.data.middle_name} {this.state.data.last_name}
                          </Typography>
                          <Typography>{moment(this.state.data.dob).format('MMM D, YYYY')}</Typography>
                        </Box>
                      )}
                      <div>
                        <Button
                          onClick={this.goToScan}
                          sx={{
                            ...classes.button,
                            ...classes.scanButton,
                            ...(this.state.scanned && classes.scanVisible),
                          }}
                          color="primary"
                          variant="contained"
                          aria-label="scan"
                          disableElevation
                        >
                          <SvgIcon>
                            <path d="M.5 6c.276 0 .5-.224.5-.5V1h4.5c.276 0 .5-.224.5-.5S5.776 0 5.5 0h-5C.224 0 0 .224 0 .5v5c0 .276.224.5.5.5zM23.5 0h-5c-.276 0-.5.224-.5.5s.224.5.5.5H23v4.5c0 .276.224.5.5.5s.5-.224.5-.5v-5c0-.276-.224-.5-.5-.5zM23.5 18c-.276 0-.5.224-.5.5V23h-4.5c-.276 0-.5.224-.5.5s.224.5.5.5h5c.276 0 .5-.224.5-.5v-5c0-.276-.224-.5-.5-.5zM.5 24h5c.276 0 .5-.224.5-.5s-.224-.5-.5-.5H1v-4.5c0-.276-.224-.5-.5-.5s-.5.224-.5.5v5c0 .276.224.5.5.5zM19.875 6H4.125C3.505 6 3 6.505 3 7.125v9.75C3 17.495 3.505 18 4.125 18h15.75c.62 0 1.125-.505 1.125-1.125v-9.75C21 6.505 20.495 6 19.875 6zM3.75 9h16.5v1.5H3.75V9zm.375-2.25h15.75c.207 0 .375.168.375.375V8.25H3.75V7.125c0-.207.168-.375.375-.375zm15.75 10.5H4.125c-.207 0-.375-.168-.375-.375V11.25h16.5v5.625c0 .207-.168.375-.375.375z" />
                            <path d="M18.667 15h-1.334c-.184 0-.333.224-.333.5s.15.5.333.5h1.334c.184 0 .333-.224.333-.5s-.15-.5-.333-.5zM15.667 15h-1.334c-.184 0-.333.224-.333.5s.15.5.333.5h1.334c.184 0 .333-.224.333-.5s-.15-.5-.333-.5zM12.667 15h-1.334c-.184 0-.333.224-.333.5s.15.5.333.5h1.334c.184 0 .333-.224.333-.5s-.15-.5-.333-.5zM9.667 15H8.333c-.184 0-.333.224-.333.5s.15.5.333.5h1.334c.184 0 .333-.224.333-.5s-.15-.5-.333-.5z" />
                          </SvgIcon>
                        </Button>
                      </div>
                    </Box>
                    <Box marginTop={1} display="flex" justifyContent="flex-end">
                      <MLink href="#" onClick={this.clear}>
                        Clear
                      </MLink>
                    </Box>
                    <Box marginTop={5} display="flex" justifyContent="center">
                      <Button
                        disabled={!this.state.scanned || this.state.scanning}
                        color="primary"
                        onClick={this.scan}
                        className={clsx(classes.button, classes.dialogButton, classes.submitScanButton)}
                        variant="contained"
                      >
                        Confirm Identification
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Container>
            {(!settings?.hide_footer || settings.hide_footer === 'false') && (
              <FooterContainer client={client} shipment={shipment} settings={settings} />
            )}
          </Container>
        )}
        <Routes>
          <Route path="scan" element={<ScanBox onConfirmScan={this.onConfirmScan} token={this.state.token} navigate={navigate} location={location} />} />
        </Routes>
      </div>
    );
  }
}

const ScanCompose = compose(inject('store'), observer)(ScanContainer);

// Wrapper component to provide router hooks to class component
function ScanContainerWrapper(props) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  return <ScanCompose {...props} match={{ params }} navigate={navigate} location={location} />;
}

export default withUseStyles(withMediaQuery()(ScanContainerWrapper));
